import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import api from '../../../services/api';
import { getSubdomainUsername, getUsernameSyncOnly } from '../../../utils/subdomain';
import { buildMediaUrl } from '../../../utils/mediaUrl';

const PublicGallery = () => {
  const { username: urlUsername } = useParams();
  const subdomainUser = getSubdomainUsername();
  const cachedCustomDomainUser = getUsernameSyncOnly();
  const username = urlUsername || subdomainUser || cachedCustomDomainUser;

  console.log('PublicGallery: username detection:', { urlUsername, subdomainUser, cachedCustomDomainUser, finalUsername: username });

  const [data, setData] = useState({ section: null, items: [] });
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/business/gallery/public/${username}/`);
        // API returns array directly
        const items = Array.isArray(res.data) ? res.data : (res.data.items || []);
        setData({
          section: res.data.section || {},
          items: items
        });
      } catch (err) {
        console.error('Error fetching gallery:', err);
      } finally {
        setLoading(false);
      }
    };
    if (username) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [username]);

  if (loading) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{
          width: '2rem', height: '2rem',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto'
        }} />
      </div>
    );
  }

  const bgColor = data.section?.background_color || '#1e293b';

  if (!data.items.length) {
    return (
      <div id="section-gallery" style={{ padding: '4rem 2rem', textAlign: 'center', backgroundColor: bgColor }}>
        <p style={{ color: 'rgba(255,255,255,0.5)' }}>No gallery images available yet.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '4rem 2rem', backgroundColor: bgColor }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
            {data.section?.title || 'Gallery'}
          </h2>
          {data.section?.subtitle && (
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem' }}>
              {data.section.subtitle}
            </p>
          )}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: '1rem'
        }}>
          {data.items.map((item, index) => (
            <div
              key={item.id || index}
              onClick={() => setSelectedImage(item)}
              style={{
                position: 'relative',
                aspectRatio: '4/3',
                borderRadius: '0.75rem',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'transform 0.2s'
              }}
            >
              <img
                src={buildMediaUrl(item.image)}
                alt={item.title || `Gallery image ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              {item.title && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '1rem',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                  color: '#fff',
                  fontWeight: '500'
                }}>
                  {item.title}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <div
            onClick={() => setSelectedImage(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '2rem'
            }}
          >
            <button
              onClick={() => setSelectedImage(null)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              <X size={32} />
            </button>
            <img
              src={buildMediaUrl(selectedImage.image)}
              alt={selectedImage.title || 'Gallery image'}
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                objectFit: 'contain',
                borderRadius: '0.5rem'
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicGallery;
